import { decodeInbound } from './parent-bridge';

/**
 * Round-trips client-side tool calls between the iframe (where the LLM
 * stream produces them) and the parent loader (where the customer's
 * `RuneAgent.push({ handlers })` map actually lives).
 *
 *   onToolCall fires here in the iframe
 *      ⇣ requestToolCall postMessage to parent
 *   loader.executeToolCall runs handler with try/catch + timeout
 *      ⇣ postMessage tool-call-response
 *   iframe listener resolves the awaited Promise
 *      ⇣ caller does addToolOutput
 *   AI SDK's sendAutomaticallyWhen detects all tool results are in,
 *   resubmits to the model
 *
 * Why a separate listener (vs. piggybacking on App.tsx's onMessage)?
 * The App-level listener is coupled to the handshake state machine and
 * is locked to the parent origin pinned at init time. The bridge is a
 * narrower concern — pure request/response over postMessage — so keeping
 * it self-contained makes it easier to test in isolation. Both listeners
 * coexist fine; each filters the message types it cares about.
 *
 * Safety:
 *   - Inbound messages are gated on `e.source === window.parent` AND
 *     `e.origin === parentOrigin` AND the strict shape decoder.
 *   - Outbound `tool-call-request` is targeted at parentOrigin (never '*').
 *   - The internal timeout exists ON TOP of the loader-side timeout so the
 *     iframe doesn't hang indefinitely if the parent listener was torn
 *     down or never installed (e.g. customer's CSP killed widget.js).
 */

const BRIDGE_TIMEOUT_MS = 35_000;

type ToolCallResult = { output: unknown } | { error: string };

type Pending = {
  resolve: (result: ToolCallResult) => void;
  timer: ReturnType<typeof setTimeout>;
};

export class ToolBridge {
  private readonly pending = new Map<string, Pending>();
  private readonly listener: (e: MessageEvent) => void;

  constructor(private readonly parentOrigin: string) {
    this.listener = (e) => this.onMessage(e);
    window.addEventListener('message', this.listener);
  }

  destroy(): void {
    window.removeEventListener('message', this.listener);
    // Don't just abandon the pending Promises — resolve each with a
    // structured error. If ChatPanel unmounts mid-call (session expiry,
    // user closed widget), `onToolCall` is awaiting these. Letting them
    // hang would leak memory tied to the dead React tree and would also
    // leave the AI SDK's internal tool-call state unresolved if React
    // re-mounts before GC runs.
    for (const p of this.pending.values()) {
      clearTimeout(p.timer);
      p.resolve({ error: 'bridge destroyed' });
    }
    this.pending.clear();
  }

  requestToolCall(params: {
    toolCallId: string;
    name: string;
    input: unknown;
    autoRun: boolean;
  }): Promise<ToolCallResult> {
    return new Promise<ToolCallResult>((resolve) => {
      const timer = setTimeout(() => {
        if (!this.pending.has(params.toolCallId)) return;
        this.pending.delete(params.toolCallId);
        resolve({ error: `bridge timeout after ${BRIDGE_TIMEOUT_MS}ms` });
      }, BRIDGE_TIMEOUT_MS);

      this.pending.set(params.toolCallId, { resolve, timer });

      window.parent?.postMessage(
        { type: 'agents/tool-call-request', payload: params },
        this.parentOrigin,
      );
    });
  }

  private onMessage(e: MessageEvent): void {
    if (e.source !== window.parent) return;
    if (e.origin !== this.parentOrigin) return;
    const msg = decodeInbound(e.data);
    if (!msg || msg.type !== 'agents/tool-call-response') return;

    const pending = this.pending.get(msg.payload.toolCallId);
    if (!pending) return;
    this.pending.delete(msg.payload.toolCallId);
    clearTimeout(pending.timer);

    if ('error' in msg.payload && typeof msg.payload.error === 'string') {
      pending.resolve({ error: msg.payload.error });
    } else {
      pending.resolve({ output: msg.payload.output });
    }
  }
}
